import React, { useState } from "react";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { Spinner, SkeletonRows } from "../../Components/Common/LoadingStates";
import { getAlertsAdmin, deleteAlertAdmin } from "../../helpers/fakebackend_helper";
import { useCachedRequest, invalidateCache } from "../../helpers/cachedFetch";

const formatDate = (d) => {
  if (!d) return "—";
  try {
    const date = new Date(d);
    return isNaN(date.getTime()) ? d : date.toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return d;
  }
};

const formatNum = (n) => {
  if (n == null || n === "") return "—";
  const x = Number(n);
  return isNaN(x) ? n : x.toLocaleString(undefined, { maximumFractionDigits: 4 });
};

const AppAlerts = ({ embedded = false } = {}) => {
  document.title = "Alerts | BiorGov - Admin";

  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const req = useCachedRequest({
    key: "alerts:admin:200",
    fetcher: () => getAlertsAdmin({ limit: 200, offset: 0 }),
  });
  const alerts = Array.isArray(req.data?.alerts) ? req.data.alerts : [];
  const pagination = req.data?.pagination ?? { total: 0, limit: 200, offset: 0 };
  const loading = req.loading;

  const fetchAlerts = async () => {
    invalidateCache("alerts:");
    return req.refetch();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this alert?")) return;
    setDeletingId(id);
    setError(null);
    try {
      await deleteAlertAdmin(id);
      invalidateCache("alerts:");
      await req.refetch();
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className={embedded ? "" : "min-h-screen"}>
      <div className={embedded ? "w-full min-w-0" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6"}>
        {!embedded && <BreadCrumb title="Alerts" pageTitle="Dashboard" />}

        <div className="mt-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <p className="text-sm text-bl-text-soft m-0">Price alerts created by users across all chains.</p>
            <button
              type="button"
              onClick={() => fetchAlerts()}
              disabled={loading}
              className="bl-btn-secondary flex items-center gap-2 disabled:opacity-50"
            >
              <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`}></i>
              Refresh
            </button>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <div className="flex items-center gap-2 text-red-300 text-sm">
                <i className="ri-error-warning-line"></i>
                {error}
              </div>
            </div>
          )}

          <div className="bl-glass overflow-hidden">
            {!loading && alerts.length === 0 ? (
              <div className="p-8 text-center text-gray-400">No alerts found.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-bl-accent/10 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      <th className="px-4 py-3">Id</th>
                      <th className="px-4 py-3">User</th>
                      <th className="px-4 py-3">Coin</th>
                      <th className="px-4 py-3">Condition</th>
                      <th className="px-4 py-3 text-right">Target / Bounds</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Created</th>
                      <th className="px-4 py-3 w-20"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {loading && <SkeletonRows rows={5} cols={8} />}
                    {!loading && alerts.map((a) => (
                      <tr key={a.id} className="hover:bg-bl-accent/10/30">
                        <td className="px-4 py-3 text-gray-100">{a.id}</td>
                        <td className="px-4 py-3 text-gray-300 font-mono text-xs">{a.user_id || "—"}</td>
                        <td className="px-4 py-3">
                          <span className="font-medium text-gray-100">{a.coin_symbol || "—"}</span>
                          {a.coin_name && <span className="text-gray-400 ml-1">({a.coin_name})</span>}
                        </td>
                        <td className="px-4 py-3 text-gray-300">{a.condition || "—"}</td>
                        <td className="px-4 py-3 text-right text-gray-300">
                          {a.target_price != null && <span>Target: {formatNum(a.target_price)}</span>}
                          {(a.upper_bound != null || a.lower_bound != null) && (
                            <span className="block text-xs">[{formatNum(a.lower_bound)} – {formatNum(a.upper_bound)}]</span>
                          )}
                          {a.percentage_threshold != null && <span className="block text-xs">%: {formatNum(a.percentage_threshold)}</span>}
                          {a.target_price == null && a.upper_bound == null && a.lower_bound == null && a.percentage_threshold == null && "—"}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${a.is_active ? "bg-green-500/16 text-green-400" : "bg-gray-700 text-gray-400"}`}>
                            {a.is_active ? "Active" : "Inactive"}
                          </span>
                          {a.is_triggered && (
                            <span className="ml-1 inline-flex px-2 py-0.5 rounded text-xs font-medium bg-amber-500/16 text-amber-300">Triggered</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-400">{formatDate(a.created_at)}</td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => handleDelete(a.id)}
                            disabled={deletingId === a.id}
                            className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg disabled:opacity-50"
                            title="Delete"
                          >
                            <i className={`ri-delete-bin-line ${deletingId === a.id ? "animate-pulse" : ""}`}></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {!loading && pagination.total > 0 && (
              <div className="px-4 py-3 border-t border-gray-700 text-xs text-gray-400">
                Total: {pagination.total} alert{pagination.total !== 1 ? "s" : ""}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppAlerts;
